<script setup>
import { defineProps, reactive, ref, computed, onMounted } from 'vue';
import axios from 'axios';
import Paginator from '@/components/datatables/Paginator.vue';
import Multiselect from "@vueform/multiselect";
import "@vueform/multiselect/themes/default.css";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { formatDateToWeb } from '../../helpers/date-functions';
import { formatCurrency } from '../../helpers/function';
import { usePermissions } from '@/composables/usePermissions';

const props = defineProps({
    programList: Array,
    contractStatusList: Array,
})

// Initialize permissions
const permissions = usePermissions();

const rangeDateconfig = {
    mode: "range",
    dateFormat: "Y-m-d",
}

// state
const result = ref([]);
const loading = ref(false);
const message = ref('');
const statusList = ref([]);

const filters = reactive({
    search: null,
    date: null,
    program_ids: [],
    contract_status_id: "",
});
const errorMessage = ref('');

// Computed program options for Multiselect
const programOptions = computed(() => {
    if (!props.programList) return [];
    return props.programList.map(item => {
        if (typeof item === 'object' && item !== null && 'program_name' in item) {
            return { value: item.id, label: item.program_name };
        }
        if (typeof item === 'object' && item !== null && 'label' in item) {
            return item;
        }
        return { value: item.id || item, label: item.program_name || item.name || item };
    });
});

// methods
const fetchContractStatuses = async () => {
    if (props.contractStatusList && props.contractStatusList.length > 0) {
        statusList.value = props.contractStatusList;
        return;
    }
    try {
        axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
        const response = await axios.get(`${process.env.VUE_APP_API_BASEURL}contractstatus/list`);
        if (response && response.data) {
            statusList.value = response.data;
        }
    } catch (err) {
        console.error('Failed to fetch contract statuses:', err);
    }
};

const fetchData = async (page = 1) => {        
    loading.value = true;
    try {
        const params = { 
            page: page, 
            key_search: filters.search,
            date: filters.date,
            program_ids: filters.program_ids,
            program_id: filters.program_ids,
            contract_status_id: filters.contract_status_id,
        };

        const response = await axios.post(`${process.env.VUE_APP_API_BASEURL}filters/contracts`, params);
        if (response.status != 200) {
            alert(response.data.message);
        }
        result.value = response.data;
    } catch (error) {
        console.error(error);
        errorMessage.value = 'Failed to load the data! Please try again later.';
    } finally {
        loading.value = false;
    }
};

const resetForm = () => {
    filters.search = null;
    filters.date = null;
    filters.program_ids = [];
    filters.contract_status_id = "";
    errorMessage.value = '';

    fetchData();
};

const onSearchHandler = (e) => {
    e.preventDefault();    
    fetchData();
};

const downloadExcel = () => {
    if (!permissions.canExport?.value) {
        alert('You do not have permission to export data.');
        return;
    }

    const params = {
        key_search: filters.search,
        date: filters.date,
        program_ids: filters.program_ids,
        program_id: filters.program_ids,
        contract_status_id: filters.contract_status_id,
    };
    if (params.date == null) {
        alert('Please select date');
        return;
    }

    loading.value = true;
    message.value = 'Data being exported, please wait...';

    axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
    axios
        .post(process.env.VUE_APP_API_BASEURL + 'contract/export', params, { responseType: 'blob' })
        .then(response => {
            if (response) {
                var fileURL = window.URL.createObjectURL(new Blob([response.data]));
                var fileLink = document.createElement('a');

                fileLink.href = fileURL;
                fileLink.setAttribute('download', 'Contracts List.xlsx');
                document.body.appendChild(fileLink);

                fileLink.click();
            }
        })
        .catch(error => {
            console.log(error);
            alert('Error downloading file');
        })
        .finally(() => {
            loading.value = false;
            message.value = '';
        });
};

// lifecycle
onMounted(() => {
    axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
    fetchContractStatuses();
    fetchData();
});
</script>

<template>
    <div>
        <!-- searching -->
        <div class="card-body border-bottom-dashed border-bottom">
            <form @submit="onSearchHandler">
                <!-- Top Row: Search, Date, Program Multiselect -->
                <div class="row g-3 mb-3">
                    <div class="col-xl-5">
                        <div class="search-box">
                            <input type="text" class="form-control search"
                                placeholder="Search for Customer Name, Order Number, Contract Number" v-model="filters.search"/>
                            <i class="ri-search-line search-icon"></i>
                        </div>
                    </div>
                    <div class="col-xl-7">
                        <div class="row g-3">
                            <div class="col-sm-6">
                                <div>
                                    <flat-pickr v-model="filters.date" placeholder="Select Contract Signed Date" :config="rangeDateconfig"
                                        class="form-control"></flat-pickr>
                                </div>
                            </div>                  
                        
                            <div class="col-sm-6">
                                <Multiselect 
                                    v-model="filters.program_ids" 
                                    placeholder="Select Program" 
                                    mode="tags" 
                                    :close-on-select="false" 
                                    :searchable="true"
                                    :create-option="false" 
                                    :options="programOptions" 
                                    class="contract-program-multiselect"
                                />
                            </div>                  
                        </div>
                    </div>
                </div>

                <!-- Bottom Row: Contract Status & Filter Action Buttons -->
                <div class="row g-3 align-items-center">
                    <div class="col-xl-4 col-md-5 col-sm-6">
                        <select v-model="filters.contract_status_id" 
                                class="form-select"
                                :class="{'text-muted': !filters.contract_status_id}">
                            <option value="" class="text-dark">All Statuses</option>
                            <option v-for="status in statusList" :key="status.id" :value="status.id" class="text-dark">
                                {{ status.name }}
                            </option>
                        </select>
                    </div>

                    <div class="col-xl-8 col-md-7 col-sm-6">
                        <div class="d-flex justify-content-end gap-2">
                            <button type="submit" class="btn btn-primary px-4" :disabled="loading">
                                <i class="ri-equalizer-fill me-1 align-bottom"></i> Search
                            </button>
                            <button type="button" @click="resetForm" class="btn btn-warning px-4 text-white" :disabled="loading">
                                <i class="ri-refresh-fill me-1 align-bottom"></i> Reset
                            </button>
                            <button type="button" 
                                    @click="downloadExcel()" 
                                    class="btn btn-success px-4"  
                                    :disabled="loading || !permissions.canExport?.value"
                                    :title="!permissions.canExport?.value ? 'No export permission' : 'Export to Excel'">
                                <i class="ri-file-excel-2-line me-1 align-bottom"></i> Export
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
        <div v-if="message" class="alert alert-info mt-2 mb-0">
            {{ message }}
        </div>
        <div class="table-responsive mt-3">
            <table class="table table-striped table-nowrap mb-0">
                <thead class="table-secondary">
                    <tr>
                        <th>#.</th>
                        <th scope="col">Contract Number</th>
                        <th scope="col">Customer Name</th>
                        <th scope="col">Contract Status</th>
                        <th scope="col">SKU</th>
                        <th scope="col">IMEI</th>
                        <th scope="col">Enrolled MDM</th>
                        <th scope="col">Order Number</th>
                        <th scope="col">Program</th>
                        <th scope="col">Device Name</th>
                        <th scope="col">Monthly Subscription</th>
                        <th scope="col">Contract Signed Date</th>
                    </tr>
                </thead>
                <tbody>                        
                    <tr v-for="(item, index) in result.data" :key="item.contract_id || item.id">
                        <td>{{ index + (result.from || 1) }}</td>
                        <td>
                            <router-link :to="'/contracts/customer-contract-details/'+item.contract_id" class="fw-medium link-secondary">
                                {{ item.contract_number }}
                            </router-link>
                        </td>                               
                        <td>{{ item.name_as_per_nric }}</td>
                        <td>
                            <span v-if="item.contract_status_name" class="badge badge-soft-info fs-11">
                                {{ item.contract_status_name }}
                            </span>
                            <span v-else-if="item.status_name" class="badge badge-soft-info fs-11">
                                {{ item.status_name }}
                            </span>
                            <span v-else class="text-muted">-</span>
                        </td>
                        <td>{{ item.sku }}</td>
                        <td>{{ item.imei }}</td>
                        <td class="text-center">
                            <span v-if="item.is_device_mdm == null" class="badge badge-soft-secondary fs-11">
                                <i class="ri-question-line align-middle me-1"></i> N/A
                            </span>
                            <span v-else-if="item.is_device_mdm == 1" class="badge badge-soft-success fs-11">
                                <i class="ri-checkbox-circle-line align-middle me-1"></i> Yes
                            </span>
                            <span v-else class="badge badge-soft-danger fs-11">
                                <i class="ri-close-circle-line align-middle me-1"></i> No
                            </span>
                        </td>
                        <td>
                            <router-link :to="'/orders/customer-order-details/'+item.cid" class="fw-medium link-secondary">
                                {{ item.orderId }}
                            </router-link>
                        </td>
                        <td>{{ item.program_name }}</td>
                        <td>{{ item.product_name }}</td>
                        <td style="text-align: right;">{{ formatCurrency(item.price) }}</td>
                        <td style="text-align: center;">{{ formatDateToWeb(item.created_at, 'DD-MM-YYYY, H:mm') }}</td>
                    </tr>                    
                    <tr v-if="errorMessage">
                        <td colspan="12" style="text-align: center;">{{ errorMessage }}</td>
                    </tr>
                    <tr v-if="result.data != undefined && result.data.length == 0">
                        <td colspan="12" style="text-align: center;">No data</td>
                    </tr>
                </tbody>
            </table>
            <div class="mt-4" style="display: flex; justify-content: space-between; align-items: center;">
                <div>Showing {{ result.from || 0 }} to {{ result.to || 0 }} of {{ result.total || 0 }} results</div>
                <Paginator :data="result" :limit="1" @pagination-change-page="fetchData"></Paginator>
            </div>
        </div>
    </div>
</template>

<style scoped>
:deep(.contract-program-multiselect .multiselect-tags) {
    max-height: 90px;
    overflow-y: auto;
}
</style>